import { generateGuid } from "@entrilia/shared/utilities/generateGuid.ts";
import { financialStatementGeneralSettingsSchema } from "../../../../../../../../api/schemas/biClientSchema.ts";
import type {
  DimensionDescriptor,
  FinancialStatementConfiguration,
  MeasureDescriptor,
  MetricDescriptor,
  StatementAxisConfiguration,
  StatementDataColumnConfiguration,
  StatementLineConfiguration,
} from "../../../../../../../../api/types/svTypes.ts";
import type {
  FinancialStatementFieldState,
  StatementAxisState,
  StatementLineState,
} from "../../../../store/types/fieldsState.types.ts";
import { isDimension, isMeasure } from "../../../../utilities/fieldsHelper.ts";
import { readsPeriodFromField } from "../../financialStatement/utils/statementLines.ts";
import { configurationToConditions, valueToConfiguration } from "./configurations.ts";
import { configurationToColumns, configurationToRows, configurationToValues } from "./pivotConfigurations.ts";

type StatementStateSource = Pick<
  FinancialStatementFieldState,
  "conditions" | "lines" | "axes" | "dataColumns" | "settings"
>;

export const createStatementDataColumn = (): StatementDataColumnConfiguration => ({
  guid: generateGuid(),
  columnType: "Data",
  customConditions: [],
  linkedConditions: [],
  formulaParameters: [],
  blankIfMissingValue: false,
});

const lineToConfiguration = ({
  field,
  dimension,
  unresolvedField,
  unresolvedDimension,
  ...line
}: StatementLineState): StatementLineConfiguration => {
  if (!readsPeriodFromField(line.lineType)) {
    return line;
  }
  const { dateRange, ...rest } = line;
  const fieldConfiguration = field ? valueToConfiguration(field) : unresolvedField;
  return {
    ...rest,
    field: fieldConfiguration && { ...fieldConfiguration, dateRange },
    dimension: dimension?.config ?? unresolvedDimension,
  };
};

export const createFinancialStatementReportConfiguration = (
  state: StatementStateSource
): FinancialStatementConfiguration => ({
  conditions: state.conditions.map((condition) => condition.config),
  lines: state.lines.map(lineToConfiguration),
  columns: {
    axes: state.axes.map((axis) => ({ field: axis.field.config, showTotal: axis.showTotal })),
    dataColumns: state.dataColumns,
  },
  settings: { ...state.settings },
});

const configurationToLine = (
  { field, dimension, ...line }: StatementLineConfiguration,
  dimensions: DimensionDescriptor[],
  measures: MeasureDescriptor[],
  metrics: MetricDescriptor[]
): StatementLineState => {
  if (!readsPeriodFromField(line.lineType)) {
    return line;
  }
  const measureField = field && configurationToValues([field], measures, metrics, dimensions).find(isMeasure);
  const dimensionField = dimension && configurationToRows([dimension], dimensions, measures).find(isDimension);
  return {
    ...line,
    dateRange: field?.dateRange ?? undefined,
    field: measureField,
    dimension: dimensionField,
    unresolvedField: measureField ? undefined : field,
    unresolvedDimension: dimensionField ? undefined : dimension,
  };
};

const configurationToAxes = (
  axes: StatementAxisConfiguration[],
  dimensions: DimensionDescriptor[],
  measures: MeasureDescriptor[]
): StatementAxisState[] =>
  axes.flatMap((axis) => {
    const [field] = configurationToColumns([axis.field], dimensions, measures);
    return field ? [{ field, showTotal: axis.showTotal }] : [];
  });

export const configurationToFinancialStatementState = (
  configuration: FinancialStatementConfiguration,
  dimensions: DimensionDescriptor[],
  measures: MeasureDescriptor[],
  metrics: MetricDescriptor[]
): StatementStateSource => ({
  conditions: configurationToConditions(configuration.conditions, dimensions),
  lines: configuration.lines.map((line) => configurationToLine(line, dimensions, measures, metrics)),
  axes: configurationToAxes(configuration.columns.axes, dimensions, measures),
  dataColumns: configuration.columns.dataColumns,
  settings: financialStatementGeneralSettingsSchema.parse(configuration.settings),
});
